import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CircleDot, X } from 'lucide-react';
import { Game, PracticeShot } from '../types/curling';
import { getPracticeSession } from '../utils/storage';
import { computeZone8Data, Zone8Entry } from '../utils/sheetVisualizerData';

interface SheetVisualizerProps {
  game: Game;
}

const curlDisplay = (v: number) => `${Math.abs(v).toFixed(1)} ft`;

/**
 * Trigger button + modal showing a schematic house with curl/weight arrows
 * per (broom side, turn) quadrant. Self-contained — loads its own practice
 * shots and computes its own data from the game — so it can be dropped onto
 * any page that has a Game: the Sheet Map page, the live game summary, and
 * the shot entry screen.
 */
export function SheetVisualizer({ game }: SheetVisualizerProps) {
  const [practiceShots, setPracticeShots] = useState<PracticeShot[]>([]);
  const [show, setShow] = useState(false);
  const [diagramEnd, setDiagramEnd] = useState<'away' | 'home'>('away');

  useEffect(() => {
    let cancelled = false;
    getPracticeSession(game.id)
      .then(session => { if (!cancelled) setPracticeShots(session.shots ?? []); })
      .catch(error => console.error('Failed to load practice shots for the Sheet Visualizer:', error));
    return () => { cancelled = true; };
  }, [game.id]);

  const zone8Data = computeZone8Data(game, practiceShots);
  const hasData = zone8Data.some(z => z.shots > 0);
  const isLiveGame = !game.isComplete;

  if (!hasData) return null;

  // Schematic house from just above the hog line to the back boards, with a
  // curved arrow per (side, turn) quadrant sized and colored by that
  // quadrant's curl, labeled with curl and draw-to-tee-line time. While a
  // game is in progress this shows the last few shots in each quadrant rather
  // than the whole-game average, since ice conditions drift and the next shot
  // cares about recent behavior, not history — once the game is complete it
  // switches to the full-game average.
  const renderDiagram = () => {
    const entries = zone8Data.filter(z => z.endType === diagramEnd);
    const getEntry = (side: 'left' | 'right', direction: 'left-right' | 'right-left') =>
      entries.find(e => e.side === side && e.direction === direction);

    const W = 340;
    const H = 500;
    const centerX = W / 2;
    const hogLineY = 40;
    const teeLineY = 300;
    const backLineY = 410;
    const boardsY = H - 30;
    const radii = { twelve: 110, eight: 75, four: 40, button: 8 };

    // Away end flips vertically — hog line at the bottom, back boards at the top —
    // since it's the far end of the same sheet, viewed from the other direction.
    // Left/right never change: only y-coordinates run through this mapping.
    const flip = diagramEnd === 'away';
    const Y = (y: number) => (flip ? H - y : y);

    // Each arrow starts roughly midway between the center line and the sheet
    // edge (offset +/-10px per pair so the two parallel straight runs on a half
    // don't sit exactly on top of each other), travels straight down parallel to
    // the center line, then bends toward the center or the edge — a clockwise
    // turn always drifts right, counter-clockwise always drifts left, regardless
    // of which half it started on — stopping just short of the tee line.
    const halfInset = (centerX - 20) / 2; // 75: midway between center line and sheet edge
    const leftMid = centerX - halfInset;
    const rightMid = centerX + halfInset;
    const startY = hogLineY + 50;
    const breakY = teeLineY - radii.twelve - 10; // just before the house, where the curve begins
    const endY = teeLineY - 15; // arrow tip, just short of the tee line

    const arrowSpecs: Array<{
      key: string;
      startX: number;
      sign: 1 | -1;
      isCw: boolean;
      color: string;
      markerId: string;
      entry?: Zone8Entry;
    }> = [
      { key: 'left-cw', startX: leftMid + 10, sign: 1, isCw: true, color: '#f87171', markerId: 'curl-arrow-red',
        entry: getEntry('left', 'left-right') },
      { key: 'left-ccw', startX: leftMid - 10, sign: -1, isCw: false, color: '#60a5fa', markerId: 'curl-arrow-blue',
        entry: getEntry('left', 'right-left') },
      { key: 'right-ccw', startX: rightMid - 10, sign: -1, isCw: false, color: '#60a5fa', markerId: 'curl-arrow-blue',
        entry: getEntry('right', 'right-left') },
      { key: 'right-cw', startX: rightMid + 10, sign: 1, isCw: true, color: '#f87171', markerId: 'curl-arrow-red',
        entry: getEntry('right', 'left-right') },
    ];
    // Labels sit near each arrow's tail (start), not its tip. The cw/ccw pair on
    // the same half start only 20px apart — too close for two 60px-wide boxes
    // side by side — so they're stacked at two different heights within the
    // straight run instead; same-height labels are always a full half (~150px)
    // apart, so this is collision-free regardless of curl magnitude.
    const ccwLabelY = startY + 14;
    const cwLabelY = startY + 58;

    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <defs>
          <marker id="curl-arrow-red" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#f87171" />
          </marker>
          <marker id="curl-arrow-blue" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#60a5fa" />
          </marker>
        </defs>

        {/* Sheet background */}
        <rect x={20} y={20} width={W - 40} height={H - 40} rx={8} fill="#0f2942" stroke="rgba(255,255,255,0.15)" />

        {/* House rings, centered on the button at the tee line */}
        <circle cx={centerX} cy={Y(teeLineY)} r={radii.twelve} fill="#2563eb" fillOpacity={0.25} stroke="#3b82f6" strokeWidth={1.5} />
        <circle cx={centerX} cy={Y(teeLineY)} r={radii.eight} fill="#0f2942" stroke="#e2e8f0" strokeWidth={1.5} />
        <circle cx={centerX} cy={Y(teeLineY)} r={radii.four} fill="#dc2626" fillOpacity={0.35} stroke="#ef4444" strokeWidth={1.5} />
        <circle cx={centerX} cy={Y(teeLineY)} r={radii.button} fill="#ef4444" />

        {/* Hog line */}
        <line x1={20} y1={Y(hogLineY)} x2={W - 20} y2={Y(hogLineY)} stroke="#f87171" strokeWidth={2.5} />
        <text x={W - 24} y={Y(hogLineY - 6)} textAnchor="end" fontSize={9} fill="#f87171">Hog Line</text>

        {/* Tee line */}
        <line x1={20} y1={Y(teeLineY)} x2={W - 20} y2={Y(teeLineY)} stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} />
        <text x={24} y={Y(teeLineY - 6)} fontSize={9} fill="rgba(255,255,255,0.5)">Tee Line</text>

        {/* Back line */}
        <line x1={20} y1={Y(backLineY)} x2={W - 20} y2={Y(backLineY)} stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} />
        <text x={24} y={Y(backLineY - 6)} fontSize={9} fill="rgba(255,255,255,0.5)">Back Line</text>

        {/* Back boards */}
        <line x1={20} y1={Y(boardsY)} x2={W - 20} y2={Y(boardsY)} stroke="#94a3b8" strokeWidth={4} />
        <text x={centerX} y={Y(H - 12)} textAnchor="middle" fontSize={9} fill="#94a3b8">Back Boards</text>

        {/* Center line: hog line to back line, splitting the two halves */}
        <line x1={centerX} y1={Y(hogLineY)} x2={centerX} y2={Y(backLineY)} stroke="rgba(255,255,255,0.3)" strokeWidth={1.5} strokeDasharray="4 3" />

        {/* Left/right orientation labels, anchored just inside the hog line (which
            itself moves with the flip) — left and right never change. */}
        {(() => {
          const sideLabelY = Y(hogLineY + 40);
          return (
            <>
              <text x={32} y={sideLabelY} textAnchor="middle" fontSize={10} fontWeight={600} fill="rgba(255,255,255,0.4)"
                transform={`rotate(-90 32 ${sideLabelY})`}>LEFT</text>
              <text x={W - 32} y={sideLabelY} textAnchor="middle" fontSize={10} fontWeight={600} fill="rgba(255,255,255,0.4)"
                transform={`rotate(90 ${W - 32} ${sideLabelY})`}>RIGHT</text>
            </>
          );
        })()}

        {/* Curl arrows, one per (side, turn) quadrant */}
        {arrowSpecs.map(spec => {
          const entry = spec.entry;
          const labelY = spec.isCw ? cwLabelY : ccwLabelY;

          if (!entry || entry.shots === 0) {
            return (
              <g key={spec.key} opacity={0.4}>
                <circle cx={spec.startX} cy={Y(startY)} r={4} fill="none" stroke={spec.color} strokeWidth={1.5} strokeDasharray="2 2" />
                <text x={spec.startX} y={Y(labelY + 4)} textAnchor="middle" fontSize={9} fill="#64748b">No data</text>
              </g>
            );
          }

          const displayCurl = isLiveGame ? (entry.recentCurl ?? 0) : entry.avgCurl;
          const timeIsEstimate = entry.timeEstimate.isEstimate;
          const timeText = `${timeIsEstimate ? '~' : ''}${entry.timeEstimate.time.toFixed(1)}s`;

          const curlOffset = Math.min(Math.max(Math.abs(displayCurl) * 14, 12), 100);
          const endX = Math.min(Math.max(spec.startX + spec.sign * curlOffset, 30), W - 30);
          // Straight run (parallel to the center line) down to breakY, then a
          // quadratic curve to the tip. The control point sits at the vertical
          // midpoint of the curve (not hugging the tip's y), so the bend sweeps
          // gradually rather than staying flat and then hooking sharply at the end.
          const controlY = (breakY + endY) / 2;
          const path = `M ${spec.startX} ${Y(startY)} L ${spec.startX} ${Y(breakY)} `
            + `Q ${spec.startX} ${Y(controlY)} ${endX} ${Y(endY)}`;

          // Box bounds are symmetric around labelY (±17) so its flipped top is just
          // Y(labelY) - 17 either way, and both text lines keep the same clearance
          // from their nearest edge regardless of which one ends up on top.
          const boxTop = Y(labelY) - 17;
          // Box touches the arrow line at one edge rather than straddling it —
          // extending in the direction the arrow eventually curves (sign) keeps
          // it from sitting on top of the line and visually hints at the bend.
          const boxLeft = spec.sign > 0 ? spec.startX : spec.startX - 60;
          const textX = boxLeft + 30;

          return (
            <g key={spec.key}>
              <path d={path} stroke={spec.color} strokeWidth={3.5} fill="none" strokeLinecap="round"
                markerEnd={`url(#${spec.markerId})`} opacity={0.9} />
              <rect x={boxLeft} y={boxTop} width={60} height={34} rx={6}
                fill="rgba(15,23,42,0.9)" stroke={spec.color} strokeWidth={1}
                strokeDasharray={timeIsEstimate ? '3 2' : undefined} />
              <text x={textX} y={Y(labelY - 6)} textAnchor="middle" fontSize={11} fontWeight={700} fill={spec.color}>
                {curlDisplay(displayCurl)}
              </text>
              <text
                x={textX}
                y={Y(labelY + 8)}
                textAnchor="middle"
                fontSize={10}
                fontStyle={timeIsEstimate ? 'italic' : 'normal'}
                fill={timeIsEstimate ? '#fbbf24' : '#cbd5e1'}
              >
                {timeIsEstimate && (
                  <title>
                    {entry.timeEstimate.source === 'mirror'
                      ? 'Estimated from the first shot on the mirrored path — not a learned value for this quadrant'
                      : 'Estimated from practice shots or the game setup time — no shots recorded here or on the mirrored path yet'}
                  </title>
                )}
                {timeText}
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <>
      <button
        onClick={() => setShow(true)}
        className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-400 text-white rounded-lg transition-colors text-sm font-medium"
      >
        <CircleDot className="w-4 h-4" />
        Sheet Visualizer
      </button>

      {show && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShow(false)}
        >
          <div
            className="bg-slate-900 border border-white/15 rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-white">Sheet Visualizer</h2>
                {isLiveGame && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold">
                    LIVE
                  </span>
                )}
              </div>
              <button
                onClick={() => setShow(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              {isLiveGame
                ? 'Estimating your next shot from the last 3 shots in each quadrant — a game-long average would hide how the ice has changed.'
                : "Average curl and draw-to-tee-line time for each quadrant across the whole game."}
            </p>

            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setDiagramEnd('away')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  diagramEnd === 'away' ? 'bg-sky-500 text-white' : 'bg-white/10 text-slate-300 hover:bg-white/15'
                }`}
              >
                Away End
              </button>
              <button
                onClick={() => setDiagramEnd('home')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  diagramEnd === 'home' ? 'bg-green-500 text-white' : 'bg-white/10 text-slate-300 hover:bg-white/15'
                }`}
              >
                Home End
              </button>
            </div>

            {renderDiagram()}

            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs text-slate-400">
              <span><span className="text-red-400 font-semibold">Red</span> = Clockwise turn (In-turn)</span>
              <span><span className="text-blue-400 font-semibold">Blue</span> = Counter-clockwise turn (Out-turn)</span>
              <span className="w-full">
                Each label is that quadrant's curl and draw-to-tee-line time
                {isLiveGame ? ', averaged over the last 3 shots' : ', averaged over the whole game'}
              </span>
              <span>
                <span className="text-amber-300 font-semibold italic">~15.1s</span> = estimated — from the first
                shot on the mirrored path, or from practice shots or the game setup time if that's empty too —
                not a real reading for this quadrant
              </span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
